/**
 * Returns the cleaned "street + house number" line for display.
 *
 * Canonical semantics (mirrored across all four happy-legal repos):
 *
 * - `houseNumber` empty/undefined  -> return trimmed `streetName`.
 * - `streetName`  empty/undefined  -> return trimmed `houseNumber`.
 * - `streetName` already ENDS WITH `houseNumber` as a WHOLE TOKEN,
 *   preceded by whitespace or "comma + whitespace", AND the character
 *   immediately before that whitespace is NOT a digit
 *   -> return trimmed `streetName` UNCHANGED (the number is preserved
 *      inside the street; do NOT strip it).
 * - Otherwise                      -> `${trimmedStreet} ${trimmedNumber}`.
 *
 * Word-boundary safe: ("Straße 212","12") -> "Straße 212 12" (never
 * false-match the substring "12" inside the larger token "212").
 *
 * Defensive helper: if Docupipe extracted the combined string
 * "Archenholdstraße 15" while `houseNumber` was also captured as "15",
 * the streetName already carries the number as a whole token so we MUST
 * keep it intact. The old "strip-and-lose" behaviour dropped the number
 * entirely on legacy data.
 */
const escapeRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function formatStreetLine(
  streetName: string | null | undefined,
  houseNumber: string | null | undefined,
): string {
  const trimmedStreet = (streetName ?? "").trim();
  const trimmedNumber = (houseNumber ?? "").trim();

  if (!trimmedNumber) {
    return trimmedStreet;
  }

  if (!trimmedStreet) {
    return trimmedNumber;
  }

  // If streetName already ends with houseNumber as a WHOLE TOKEN preceded
  // by whitespace (or comma + whitespace), and the character before that
  // whitespace is NOT a digit, the line is already complete — preserve it.
  // Example: ("Archenholdstraße 15","15")   -> "Archenholdstraße 15"
  // Example: ("Musterstraße, 12","12")      -> "Musterstraße, 12"
  // Counter-example: ("Straße 212","12")    -> "Straße 212 12"
  //   ("12" matches as string tail, but the char before "12" is "2",
  //    a digit, so we must NOT treat it as the separate house number.)
  const re = new RegExp(
    `([^\\d]),?\\s+${escapeRegExp(trimmedNumber)}\\s*$`,
    "i",
  );
  if (re.test(trimmedStreet)) {
    return trimmedStreet;
  }

  return `${trimmedStreet} ${trimmedNumber}`;
}
